import { useEffect, useRef, useState } from "react"
import { LEVEL_ITEMS, REGIONS } from "./utils.js"
import IconArrowLeft from "../images/icon-arrow-left.svg?react"
import IconChevronDown from "../images/icon-chevron-down.svg?react"
import IconCheck from "../images/icon-check.svg?react"
import FlagUSA from "../images/flag-usa.svg?react"
import FlagCIS from "../images/flag-cis.svg?react"

function Dropdown({ items, value, onChange, renderTrigger, align = "right", width = 180, disabled = false }) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)

  const selected = items.find((item) => item.value === value) || null

  useEffect(() => {
    if (!open) return
    const handle = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener("mousedown", handle)
    return () => document.removeEventListener("mousedown", handle)
  }, [open])

  useEffect(() => {
    if (disabled && open) setOpen(false)
  }, [disabled, open])

  const handleSelect = (item) => {
    setOpen(false)
    if (item.value !== value) onChange(item.value)
  }

  return (
    <div className={`dropdown-container ${disabled ? "is-disabled" : ""}`} ref={containerRef}>
      <div onClick={disabled ? undefined : () => setOpen((open) => !open)}>
        {renderTrigger({ open, selected, disabled })}
      </div>
      {open && !disabled && (
        <div className={`dropdown dropdown--${align}`} style={{ width }}>
          {items.map((item) => {
            const isSelected = item.value === value
            return (
              <button
                key={item.value}
                type="button"
                className={`dd-item ${isSelected ? "selected" : ""}`}
                onClick={() => handleSelect(item)}
              >
                {item.icon && <span className="dd-item-icon">{item.icon}</span>}
                <span className="dd-item-label">{item.label}</span>
                {isSelected && (
                  <span className="dd-item-check">
                    <IconCheck width={14} height={14} />
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

const FLAGS = { USA: FlagUSA, CIS: FlagCIS }

function getAppName() {
  try {
    return chrome.i18n.getMessage("appName") || "Equation Solver"
  } catch {
    return "Equation Solver"
  }
}

function getExtensionVersion() {
  try {
    return chrome.runtime.getManifest().version
  } catch {
    return ""
  }
}

const APP_NAME = getAppName()
const APP_VERSION = getExtensionVersion()

function Flag({ region }) {
  const Component = FLAGS[region]
  if (!Component) return null
  return <Component className="flag-icon" width={20} height={15} />
}

const REGION_ITEMS = REGIONS.map((region) => ({
  value: region,
  label: region,
  icon: <Flag region={region} />,
}))

function LevelBars({ total, index }) {
  return (
    <span className="level-bars" aria-hidden="true">
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={`level-bar ${i <= index ? "filled" : ""}`}
          style={{ height: `${((i + 1) / total) * 100}%` }}
        />
      ))}
    </span>
  )
}

const ENV_SUFFIX =
  process.env.NODE_ENV === "development" ? " [dev]" : process.env.NODE_ENV === "staging" ? " [stg]" : ""

export default function Header({ config, onSetLevel, onSetRegion, onBack, disabled = false }) {
  if (!config) return null

  const levelItems = LEVEL_ITEMS[config.region]
  const selectedLevel = levelItems.find((item) => item.value === config.level)
  const selectedLevelIndex = levelItems.findIndex((item) => item.value === config.level)

  return (
    <header className="header">
      <div className="header-row">
        <div className="header-left">
          {onBack && (
            <button className="back-arrow" onClick={onBack} aria-label="back">
              <IconArrowLeft width={18} height={18} />
            </button>
          )}
          <div className="logo">
            <a
              className="logo-icon"
              href="https://equationsolver.cc/"
              target="_blank"
              rel="noreferrer"
              aria-label="Equation Solver website"
            >
              ∑
            </a>
            <div className="logo-text-wrap">
              <h1 className="app-name">{APP_NAME}</h1>
              {APP_VERSION && (
                <div className="logo-sub">
                  {onBack ? "v" : "Version "}
                  {APP_VERSION}
                  {ENV_SUFFIX}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="header-actions">
          <Dropdown
            items={levelItems}
            value={config.level}
            onChange={onSetLevel}
            disabled={disabled}
            renderTrigger={({ open, disabled: isDisabled }) => (
              <button
                type="button"
                className={`text-dropdown-btn ${open ? "active" : ""} ${isDisabled ? "is-disabled" : ""}`}
                disabled={isDisabled}
              >
                <span>{selectedLevel?.label || "Level"}</span>
                <LevelBars total={levelItems.length} index={selectedLevelIndex} />
                <IconChevronDown width={14} height={14} />
              </button>
            )}
          />
          <Dropdown
            items={REGION_ITEMS}
            value={config.region}
            onChange={onSetRegion}
            disabled={disabled}
            renderTrigger={({ open, disabled: isDisabled }) => (
              <button
                type="button"
                className={`icon-btn ${open ? "active" : ""} ${isDisabled ? "is-disabled" : ""}`}
                aria-label="region"
                disabled={isDisabled}
              >
                <Flag region={config.region} />
              </button>
            )}
          />
        </div>
      </div>
    </header>
  )
}
